import type {
  AllUpSelection,
  BetLegType,
  BetPrices,
  BetSlipBetRequest,
  BetSlipResponse,
} from '@mobi/betslip/types'
import { BetSpecialOffer } from '@classic/Specials/Model/BetSpecialOffer'
import { post } from '@classic/Foundation/Services/ApiService'
import { ProductCode } from './Betting/selections'
import { isBetslipBetRequest } from './betslip'
import type { Campaign } from '@mobi/account/Areas/SignUp'

export type BetRequest = {
  copiedFollowMePostId?: string
} & (
  | ToteRequest
  | AllUpRequest
  | RacingSingleRequest
  | FobSingleRequest
  | MysteryProposeRequest
  | MysteryCommitRequest
  | BetSlipBetRequest
  | SameRaceMultiRequest
)

// ToteBetRequest
export interface ToteRequest {
  productPlanSeq: number
  fixtureId: string
  fixtureDate: string
  raceNumber: number
  selections: string
  investmentWin: number
  investmentPlace: number
  betTransactionId: string
  useBonusCash?: boolean
}

export function isToteRequest(value: BetRequest): value is ToteRequest {
  return (value as ToteRequest).selections !== undefined
}

// ToteAllUpBetRequest
export interface AllUpRequest {
  productPlanSeq: number
  fixtureId: string
  fixtureDate: string
  raceDetails: AllUpSelection['details']
  formulas: AllUpSelection['formulas']
  investment: number
  betTransactionId: string
}

export function isAllUpRequest(value: BetRequest): value is AllUpRequest {
  return (value as AllUpRequest).formulas !== undefined
}

export interface ToteBetResponse {
  ticketNumber: number
  betCost: number
  betPlacedTime: Date
  accountBalance: number
  divXAnnotations: string
  numberOfCombinations: number
  campaignActivatedInd: boolean
  bonusBet: number
  amountPaid: number
}

// tote mystery proposal
export interface MysteryProposeRequest {
  fixtureDate: string
  fixtureId: string
  raceNumber: number
  productCode: string
  option: number
  numberOfBets: number
  investmentWin?: number
  investmentPlace?: number
  useBonusCash?: boolean
}

export function isMysteryProposalRequest(value: BetRequest): value is MysteryProposeRequest {
  return (value as MysteryProposeRequest).option !== undefined
}

export interface MysteryProposeResponse {
  accountBalance: number
  metaData: string
  bonusBalanceAvailable: number
  bets: MysteryProposeBet[]
}

export interface MysteryProposeBet {
  id: number
  betCost: number
  divXAnnotations: string
  metaData: string
  betLines: MysteryProposeBetLine[]
  productCode: keyof typeof ProductCode
  isFlexi: boolean
}

export interface MysteryProposeBetLine {
  dividend: number
  poolCode: string
  selections: string
  isAllways: boolean
  legs: MysteryProposeBetLineLeg[]
}

export interface MysteryProposeBetLineLeg {
  legNumber: number
  raceNumber: number
  acceptors: MysteryProposeAcceptor[]
}

export interface MysteryProposeAcceptor {
  name: string
  number: number
}

// tote mystery commit
export interface MysteryCommitRequest {
  proposalIds: number[]
  metaData: string
  useBonusCash?: boolean
}

export function isMysteryCommitRequest(value: BetRequest): value is MysteryCommitRequest {
  return (value as MysteryCommitRequest).proposalIds !== undefined
}

export function isMysteryCommitResponse(
  value: ToteBetResponse | MysteryCommitResponse | FobCommitResponse
): value is MysteryCommitResponse {
  return (value as MysteryCommitResponse).errorMessage !== undefined
}

export interface MysteryCommitResponse {
  accountBalance: number
  campaignActivatedInd: boolean
  bets: ToteBetResponse[]
  errorMessage: string | null
  errorReason: string | null
}

export function isFobSingleRacingRequest(value: BetRequest): value is FobSingleRacingRequest {
  return (value as FobSingleRacingRequest).propositionSeq !== undefined
}

interface FobSingleBase {
  responseVersion: number
  investmentWin?: number | null
  investmentPlace?: number | null
  priceWin?: number | null
  pricePlace?: number | null
  betTransactionId: string
  specialSeq?: number | null
  selectedCampaign?: Campaign
}
// FobSingleRacingBetRequest
export interface FobSingleRacingRequest extends FobSingleBase {
  propositionSeq: string
  variantSeq?: string | null
  legType: BetLegType
  handicap?: number | null
}

export type FobSingleRequest = FobSingleRacingRequest

export function isRacingSingleRequest(value: BetRequest): value is RacingSingleRequest {
  return (
    (value as RacingSingleRequest).starterNumber !== undefined ||
    (value as RacingSingleRequest).propositionCode !== undefined
  )
}

// FobMatchedRacingBetRequest
export interface RacingSingleRequest {
  responseVersion: number
  fixtureDate: string
  fixtureId: string
  raceNumber: number
  legType: BetLegType
  priceWin: number | null
  pricePlace: number | null
  investmentWin: number
  investmentPlace: number
  starterNumber?: number | null
  specialSeq?: number | null
  propositionCode?: string | null
  betTransactionId: string
  selectedCampaign?: Campaign
}

export function isSameRaceMultiBetRequest(request: BetRequest): request is SameRaceMultiRequest {
  return !!(request as SameRaceMultiRequest).selectionPrice
}

export interface SameRaceMultiSelection {
  starter: number
  position: number
}

export interface SameRaceMultiRequest {
  responseVersion: number
  fixtureDate: string
  fixtureId: string
  raceNumber: number
  selectionPrice: number
  selections: SameRaceMultiSelection[]
  investment: number
  specialSeq?: number | null
  betTransactionId: string
  selectedCampaign?: Campaign
}

/**
 * @deprecated Stop using enums, they offer a bad DX, reverse lookups are unreliable at best and getting keys from them is near impossible. Use literals instead, or use a mapping
 */
export enum BetErrorType {
  Unspecified = 0,
  Unauthorized = 1,
  InvalidBet = 2,
  IncorrectNumberOfLegs = 3,
  InsufficientFunds = 4,
  PricesChanged = 5,
  HandicapChanged = 6,
  SpecialsError = 7,
  Closed = 8,
  BetPlacementFault = 10,
  DuplicateBonusBet = 11,
  InvalidBonusBet = 12,
}

export type BetErrorTypeLiteral =
  | 'Unspecified'
  | 'Unauthorized'
  | 'InvalidBet'
  | 'IncorrectNumberOfLegs'
  | 'InsufficientFunds'
  | 'PricesChanged'
  | 'HandicapChanged'
  | 'SpecialsError'
  | 'Closed'
  | 'BetPlacementFault'
  | 'DuplicateBonusBet'
  | 'InvalidBonusBet'

const betErrorTypeMap = new Map<BetErrorType, BetErrorTypeLiteral>([
  [BetErrorType.Unspecified, 'Unspecified'],
  [BetErrorType.Unauthorized, 'Unauthorized'],
  [BetErrorType.InvalidBet, 'InvalidBet'],
  [BetErrorType.IncorrectNumberOfLegs, 'IncorrectNumberOfLegs'],
  [BetErrorType.InsufficientFunds, 'InsufficientFunds'],
  [BetErrorType.PricesChanged, 'PricesChanged'],
  [BetErrorType.HandicapChanged, 'HandicapChanged'],
  [BetErrorType.SpecialsError, 'SpecialsError'],
  [BetErrorType.Closed, 'Closed'],
  [BetErrorType.BetPlacementFault, 'BetPlacementFault'],
  [BetErrorType.DuplicateBonusBet, 'DuplicateBonusBet'],
  [BetErrorType.InvalidBonusBet, 'InvalidBonusBet'],
])

export function ensureBetErrorTypeLiteral(
  betErrorType: string | number | BetErrorType
): BetErrorTypeLiteral {
  if (typeof betErrorType === 'string') {
    // We can't be sure this is a BetErrorTypeLiteral, but for compatibility reasons it's better to assume it is
    return betErrorType as BetErrorTypeLiteral
  }

  if (betErrorTypeMap.has(betErrorType)) {
    return betErrorTypeMap.get(betErrorType) as BetErrorTypeLiteral
  }

  throw new Error(`Unable to map ${betErrorType} as a literal value`)
}

export const SingleErrorMapping = new Map<BetErrorType, string>([
  [BetErrorType.HandicapChanged, 'Handicap changed. Bet is no longer valid.'],
  [BetErrorType.InvalidBonusBet, 'Error using Bonus Bet'],
  [BetErrorType.DuplicateBonusBet, 'Bonus Bet can only be used once'],
])

export const MultiErrorMapping = new Map<string, string>([
  [BetErrorType[BetErrorType.InvalidBonusBet], 'Error using Bonus Bet'],
  [BetErrorType[BetErrorType.DuplicateBonusBet], 'Bonus Bet can only be used once'],
])

export const MultiBetFatalErrorMapping = new Map<BetErrorType, string>([
  [BetErrorType.HandicapChanged, 'HANDICAP'],
  [BetErrorType.Closed, 'CLOSED'],
])

// shared with fob single and SRM
export interface FobProposeResponse {
  prices: BetPrices
  accountBalance?: number
  specialOffers?: BetSpecialOffer[]
}

// shared with fob single and SRM
export interface FobCommitResponse {
  accountBalance?: number
  ticketNumber: number
  projectedPlacePayout: number
  projectedWinPayout: number
  placeInvestment: number
  winInvestment: number
  betPlacedTime: Date
  campaignActivatedInd: boolean
  campaignId: number | null
  campaignType: string | null
  bonusBet: number
  amountPaid: number
  hasSpecialOffer: boolean
  specialOffers: BetSpecialOffer[]
}

export interface FobSingleErrorResponse {
  code: BetResponseCode
  response: {
    message: string
    accountBalance?: number
    prices?: BetPrices
  }
}

export interface SuperPicksResponse {
  specialOffers: BetSpecialOffer[]
}

export interface SuperPicksErrorResponse {
  response: {
    message: string
  }
}

export interface GetSuperPicksRequest {
  FixtureDate?: string
  FixtureId?: string
  RaceNumber?: number
  StarterNumber?: number
  PropositionSeq?: string
}

export enum BetResponseCode {
  UnknownError,
  Unauthorized,
  InsufficientFunds,
  PriceChanged,
  HandicapChanged,
  NetworkError,
}

const httpStatusToBetResponseCode: Record<number, BetResponseCode | undefined> = {
  401: BetResponseCode.Unauthorized,
  402: BetResponseCode.InsufficientFunds,

  406: BetResponseCode.PriceChanged,
  417: BetResponseCode.HandicapChanged,
}

export const networkErrorMessage = 'Network Error'

export function proposeBet(
  request: BetRequest
): Promise<
  FobProposeResponse | ToteBetResponse | MysteryProposeResponse | BetSlipResponse[] | null
> {
  // order is VERY important because there is no runtime type guard/descriptor implemented.. instead XxxRequest relies on the presence of 'special fields' of which there is no guarantee of uniqueness
  if (isMysteryProposalRequest(request)) {
    return quickbetMysteryPropose(request)
  }
  if (isFobSingleRacingRequest(request)) {
    return quickbetFobProposeSingle(request)
  }
  if (isSameRaceMultiBetRequest(request)) {
    return quickbetSameRaceMultiPropose(request)
  }
  if (isToteRequest(request)) {
    return quickbetTotePropose(request)
  }
  if (isAllUpRequest(request)) {
    return quickbetAllUpPropose(request)
  }
  if (isRacingSingleRequest(request)) {
    return quickbetRacingProposeSingle(request)
  }
  if (isBetslipBetRequest(request)) {
    return betslipPropose(request)
  }
  return Promise.resolve(null)
}

// Order is VERY important because there is no runtime type guard/descriptor implemented.. instead XxxRequest relies on the presence of 'special fields' of which there is no guarantee of uniqueness
export function confirmBet(
  request: BetRequest
): Promise<FobCommitResponse | ToteBetResponse | MysteryCommitResponse | BetSlipResponse[] | null> {
  if (isMysteryCommitRequest(request)) {
    return quickbetMysteryCommit(request)
  }
  if (isFobSingleRacingRequest(request)) {
    return quickbetFobCommitSingle(request)
  }
  if (isSameRaceMultiBetRequest(request)) {
    return quickbetSameRaceMultiCommit(request)
  }
  if (isToteRequest(request)) {
    return quickbetToteCommit(request)
  }
  if (isAllUpRequest(request)) {
    return quickbetAllUpCommit(request)
  }
  if (isRacingSingleRequest(request)) {
    return quickbetRacingCommitSingle(request)
  }
  if (isBetslipBetRequest(request)) {
    return betslipCommit(request)
  }

  return Promise.resolve(null)
}

export function betslipCommit(request: BetSlipBetRequest): Promise<BetSlipResponse[]> {
  return placeBet<BetSlipBetRequest, BetSlipResponse[]>('/api/betslip/commit', request)
}

export function betslipPropose(request: BetSlipBetRequest): Promise<BetSlipResponse[]> {
  return placeBet<BetSlipBetRequest, BetSlipResponse[]>('/api/betslip/propose', request)
}

export function betslipRefresh(request: BetSlipBetRequest): Promise<BetSlipResponse[]> {
  return placeBet<BetSlipBetRequest, BetSlipResponse[]>('/api/betslip/refresh', request)
}

function quickbetTotePropose(request: ToteRequest): Promise<ToteBetResponse> {
  return placeBet<ToteRequest, ToteBetResponse>('/api/betting/tote/propose', request)
}

function quickbetAllUpPropose(request: AllUpRequest): Promise<ToteBetResponse> {
  return placeBet<AllUpRequest, ToteBetResponse>('/api/betting/tote/propose/allup', request)
}

function quickbetMysteryPropose(request: MysteryProposeRequest): Promise<MysteryProposeResponse> {
  return placeBet<MysteryProposeRequest, MysteryProposeResponse>(
    '/api/betting/tote/propose/mystery',
    request
  )
}

function quickbetRacingProposeSingle(request: RacingSingleRequest): Promise<FobProposeResponse> {
  return placeBet<RacingSingleRequest, FobProposeResponse>(
    '/api/betting/fob/propose/racing-single',
    request
  )
}

function quickbetFobProposeSingle(request: FobSingleRequest): Promise<FobProposeResponse> {
  return placeBet<FobSingleRequest, FobProposeResponse>('/api/betting/fob/propose/single', request)
}

function quickbetFobCommitSingle(request: FobSingleRequest): Promise<FobCommitResponse> {
  return placeBet<FobSingleRequest, FobCommitResponse>('/api/betting/fob/commit/single', request)
}
function quickbetSameRaceMultiPropose(request: SameRaceMultiRequest): Promise<FobProposeResponse> {
  return placeBet<SameRaceMultiRequest, FobProposeResponse>(
    '/api/betting/fob/propose/same-race-multi',
    request
  )
}

function quickbetSameRaceMultiCommit(request: SameRaceMultiRequest): Promise<FobCommitResponse> {
  return placeBet<SameRaceMultiRequest, FobCommitResponse>(
    '/api/betting/fob/commit/same-race-multi',
    request
  )
}

function quickbetAllUpCommit(request: AllUpRequest): Promise<ToteBetResponse> {
  return placeBet<AllUpRequest, ToteBetResponse>('/api/betting/tote/commit/allup', request)
}

function quickbetToteCommit(request: ToteRequest): Promise<ToteBetResponse> {
  return placeBet<ToteRequest, ToteBetResponse>('/api/betting/tote/commit', request)
}

function quickbetMysteryCommit(request: MysteryCommitRequest): Promise<MysteryCommitResponse> {
  return placeBet<MysteryCommitRequest, MysteryCommitResponse>(
    '/api/betting/tote/commit/mystery',
    request
  )
}

function quickbetRacingCommitSingle(request: RacingSingleRequest): Promise<FobCommitResponse> {
  return placeBet<RacingSingleRequest, FobCommitResponse>(
    '/api/betting/fob/commit/racing-single',
    request
  )
}

function placeBet<TRequest, TResponse>(url: string, request: TRequest): Promise<TResponse> {
  return post<TResponse>({ url, body: request }).catch(async error => {
    if (!error.response) {
      throw {
        code: BetResponseCode.NetworkError,
        response: { message: networkErrorMessage },
      }
    }
    const errorResponse = await error.response.json()
    throw {
      code: httpStatusToBetResponseCode[error.response.status] || BetResponseCode.UnknownError,
      response: errorResponse,
    }
  })
}

export function getSuperPicks(request: GetSuperPicksRequest): Promise<SuperPicksResponse> {
  return post<SuperPicksResponse>({ url: '/api/specials/superpicksversion2', body: request }).catch(
    async error => {
      const errorResponse = await error.response.json()
      throw {
        response: errorResponse,
      }
    }
  )
}

export function toBetErrorType(value: string | BetErrorType): BetErrorType {
  if (typeof value === 'string') {
    return BetErrorType[value as keyof typeof BetErrorType]
  }
  return value
}
